"use client";

import { CheckCircle2, Clock, DollarSign } from "lucide-react";

import { getFeatureDemoCopy } from "@/components/features/feature-demo-copy";
import { Badge } from "@/lib/components/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/lib/components/card";

import { useI18n } from "@llmgateway/i18n/react";

const providers = [
	{
		name: "OpenAI",
		models: 15,
		status: "active",
		latency: 987,
		cost: "$45.23",
	},
	{
		name: "Anthropic",
		models: 8,
		status: "active",
		latency: 1123,
		cost: "$52.18",
	},
	{
		name: "Google AI",
		models: 12,
		status: "active",
		latency: 1456,
		cost: "$18.45",
	},
	{
		name: "Together AI",
		models: 20,
		status: "active",
		latency: 654,
		cost: "$11.57",
	},
	{
		name: "Groq",
		models: 5,
		status: "active",
		latency: 432,
		cost: "$3.42",
	},
	{
		name: "xAI",
		models: 2,
		status: "active",
		latency: 876,
		cost: "$8.21",
	},
];

export function MultiProviderDemo() {
	const { locale } = useI18n();
	const copy = getFeatureDemoCopy(locale).multiProvider;

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{copy.title}</CardTitle>
					<CardDescription>{copy.description}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						{providers.map((provider) => (
							<div
								key={provider.name}
								className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
							>
								<div className="flex items-start justify-between mb-3">
									<div>
										<p className="font-semibold">{provider.name}</p>
										<p className="text-sm text-muted-foreground">
											{provider.models} {copy.models}
										</p>
									</div>
									<Badge
										variant="outline"
										className="flex items-center gap-1 text-green-600 border-green-600"
									>
										<CheckCircle2 className="h-3 w-3" />
										{copy.active}
									</Badge>
								</div>

								<div className="space-y-2 text-sm">
									<div className="flex items-center justify-between">
										<span className="flex items-center gap-1 text-muted-foreground">
											<Clock className="h-3 w-3" />
											{copy.avgLatency}
										</span>
										<span className="font-medium">{provider.latency}ms</span>
									</div>
									<div className="flex items-center justify-between">
										<span className="flex items-center gap-1 text-muted-foreground">
											<DollarSign className="h-3 w-3" />
											{copy.totalSpend}
										</span>
										<span className="font-medium">{provider.cost}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader>
						<CardTitle className="text-4xl font-bold text-center">
							19+
						</CardTitle>
						<CardDescription className="text-center">
							{copy.providerIntegrations}
						</CardDescription>
					</CardHeader>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle className="text-4xl font-bold text-center">
							100+
						</CardTitle>
						<CardDescription className="text-center">
							{copy.availableModels}
						</CardDescription>
					</CardHeader>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle className="text-4xl font-bold text-center">1</CardTitle>
						<CardDescription className="text-center">
							{copy.unifiedApi}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		</div>
	);
}
